import { useEffect, useMemo, useState } from "react";
import { useInfiniteQuery, useQueryClient } from "@tanstack/react-query";
import { Mail, Loader2 } from "lucide-react";
import { format } from "date-fns";
import { supabase } from "@/integrations/supabase/client";
import { buildUnlinkedCoachInvitations, parseCoachInvitationPage, type InvitationLeader } from "@/lib/coachInvitations";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { InviteLeaderDialog } from "./InviteLeaderDialog";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";

interface Props {
  coachId: string;
  linkedLeaders: InvitationLeader[];
}

export function CoachInvitationsCard({ coachId, linkedLeaders }: Props) {
  const queryClient = useQueryClient();
  const [selection, setSelection] = useState<{ coachId: string; email: string } | null>(null);
  const recipient = selection?.coachId === coachId ? selection.email : null;
  const [removal, setRemoval] = useState<{ coachId: string; id: string; email: string; previouslyAccepted: boolean } | null>(null);
  const [removing, setRemoving] = useState(false);
  const [removalError, setRemovalError] = useState("");
  const pendingRemoval = removal?.coachId === coachId ? removal : null;
  const history = useInfiniteQuery({
    queryKey: ["coach-invitations", coachId],
    initialPageParam: 0,
    queryFn: async ({ pageParam }) => {
      const { data, error } = await supabase.functions.invoke("list-coach-invitations", { body: { offset: pageParam } });
      if (error) throw new Error("Invitation history could not load.");
      const page = parseCoachInvitationPage(data);
      if (page.nextOffset !== null && page.nextOffset <= pageParam) throw new Error("Invitation history could not load.");
      return page;
    },
    getNextPageParam: page => page.nextOffset ?? undefined,
    retry: false,
  });
  const rows = useMemo(() => buildUnlinkedCoachInvitations(history.data?.pages.flatMap(page => page.invitations) ?? [], linkedLeaders), [history.data, linkedLeaders]);

  useEffect(() => {
    if (recipient && !rows.some(row => row.recipientEmail === recipient && !row.previouslyAccepted)) setSelection(null);
  }, [recipient, rows]);

  const refresh = () => { void queryClient.invalidateQueries({ queryKey: ["coach-invitations", coachId] }); };
  const confirmRemoval = async () => {
    if (!pendingRemoval || removing) return;
    setRemoving(true); setRemovalError("");
    try {
      const { data, error } = await supabase.rpc("revoke_pending_coach_invitation", { p_delivery_id: pendingRemoval.id });
      if (error) throw error;
      if (data !== true) throw new Error("Invitation is no longer removable.");
      setRemoval(null); refresh();
    } catch {
      setRemovalError("We couldn't confirm whether the invitation was removed. It may already have been accepted or still belong to an existing account. Refresh invitations to check.");
      refresh();
    } finally { setRemoving(false); }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-h3"><Mail className="h-5 w-5 text-muted-foreground" />Invitations</CardTitle>
        <CardDescription>See who you invited and follow up with people who have not joined yet.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {history.isPending ? <p role="status" className="flex items-center gap-2 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" />Loading invitations…</p> : null}
        {history.isError ? (
          <div role="alert" className="space-y-2 text-sm">
            <p>Invitation history could not load. Your existing leaders are still available.</p>
            <Button variant="outline" size="sm" onClick={() => void (history.isFetchNextPageError ? history.fetchNextPage() : history.refetch())}>Retry invitations</Button>
          </div>
        ) : null}
        {!history.isPending && !history.isError && rows.length === 0 ? (
          <p className="text-sm text-muted-foreground">{history.hasNextPage ? "More invitation history is available below." : "No outstanding invitations. Accepted leaders appear in your leader list."}</p>
        ) : null}
        {rows.length > 0 ? <ul className="divide-y divide-border">
          {rows.map(row => <li key={row.recipientEmail} className="flex flex-col gap-3 py-4 first:pt-0 last:pb-0 sm:flex-row sm:items-center sm:justify-between">
            <div className="min-w-0 space-y-1">
              <p className="break-all text-sm font-medium text-foreground">{row.recipientEmail}</p>
              <div className="flex flex-wrap items-center gap-2">
                <Badge variant="secondary">{row.previouslyAccepted ? "Previously accepted" : "Not accepted yet"}</Badge>
                {row.previouslyAccepted ? <span className="text-xs text-muted-foreground">{row.canRemoveAccepted ? "No matching account found" : "Not currently linked"}</span> : null}
              </div>
              <p className="text-xs text-muted-foreground">Last invite attempt: {format(new Date(row.lastAttemptAt), "MMM d, yyyy")}</p>
            </div>
            {!row.previouslyAccepted || row.canRemoveAccepted ? <div className="flex gap-2 self-start sm:self-auto">
              {!row.previouslyAccepted ? <Button variant="outline" size="sm" aria-label={`Re-invite ${row.recipientEmail}`} onClick={() => setSelection({ coachId, email: row.recipientEmail })}>Re-invite</Button> : null}
              <Button variant="outline" size="sm" aria-label={`Remove invitation for ${row.recipientEmail}`} onClick={() => { setRemovalError(""); setRemoval({ coachId, id: row.id, email: row.recipientEmail, previouslyAccepted: row.previouslyAccepted }); }}>Remove</Button>
            </div> : null}
          </li>)}
        </ul> : null}
        {history.hasNextPage ? <Button variant="outline" size="sm" disabled={history.isFetchingNextPage} onClick={() => void history.fetchNextPage()}>{history.isFetchingNextPage ? "Loading…" : "Show more invitations"}</Button> : null}
        {rows.length > 0 ? <p className="text-xs text-muted-foreground">An invite attempt does not confirm email delivery. Re-invite lets you review the email address before sending again.</p> : null}
      </CardContent>
      <Dialog open={Boolean(pendingRemoval)} onOpenChange={open => { if (!open && !removing) { setRemoval(null); setRemovalError(""); } }}>
        <DialogContent>
          <DialogHeader><DialogTitle>Remove invitation?</DialogTitle>
            <DialogDescription>{pendingRemoval?.previouslyAccepted
              ? <>This removes the old invitation for {pendingRemoval.email} from your invitation list. Existing leaders and coaching records are not deleted.</>
              : <>This removes {pendingRemoval?.email} from your invitation list and stops them from using this invitation. You can invite them again later. Existing leaders and coaching records are not deleted.</>}</DialogDescription>
          </DialogHeader>
          {removalError ? <p role="alert" className="text-sm text-destructive">{removalError}</p> : null}
          <DialogFooter>
            <Button variant="outline" disabled={removing} onClick={() => setRemoval(null)}>Cancel</Button>
            <Button variant="destructive" disabled={removing} onClick={() => void confirmRemoval()}>{removing ? "Removing…" : "Remove invitation"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {recipient ? <InviteLeaderDialog key={`${coachId}:${recipient}`} trigger={null} open initialEmail={recipient} navigateOnSuccess={false} onOpenChange={open => { if (!open) setSelection(null); }} onInviteSent={refresh} /> : null}
    </Card>
  );
}
